export default {
  id: 'monitoring-execution',
  title: 'Monitoring Live Execution',
  content: `## Monitoring Live Execution

Once a strategy is ACTIVE, the engine evaluates it every minute. Here is how to monitor what it is doing and why.

---

## The Execution Log

Every evaluation — whether it triggered an order or not — is recorded in the **Execution Log**.

To access: tap any active strategy → **View Execution Log**

---

## Log Entry Fields

| Field | Description |
|---|---|
| Symbol | Which stock was evaluated |
| Timestamp | Exact time of evaluation |
| Decision | TRIGGERED, BLOCKED, or SKIPPED |
| Conditions met | Which conditions passed and which failed |
| Price at evaluation | The live price at the time of the check |
| Reason | Plain-English explanation of the decision |

---

## Decision Types

### TRIGGERED

All required conditions passed. A LIMIT order was automatically created for this symbol.

You will see the order appear in **Orders → Open Orders** with source: ALGO_FLOW.

After triggering, the strategy enters the **cooldown period** for that symbol — it will not re-evaluate that symbol again until the cooldown expires.

---

### BLOCKED

The conditions passed, but the order was not created for one of these reasons:

| Block reason | What happened |
|---|---|
| Cooldown active | This symbol triggered recently — cooldown window has not expired |
| Duplicate open order | An open order for this symbol already exists from this strategy |
| Daily trade limit reached | The strategy has hit its configured maximum orders for today |
| Outside market hours | Evaluation happened outside 9:15 AM – 3:30 PM IST |

---

### SKIPPED

One or more entry conditions were not met. The log shows which conditions passed and which did not, so you can understand exactly what the market was missing at that moment.

---

## Cooldown System

The cooldown prevents repeated entries on the same symbol after a trigger.

When a strategy triggers for RELIANCE with a 60-minute cooldown:
- A cooldown key is set for RELIANCE + this strategy for 60 minutes
- During those 60 minutes, all evaluations for RELIANCE under this strategy return BLOCKED
- After 60 minutes, evaluation resumes normally

This prevents the strategy from placing multiple orders on the same setup within the cooldown window.

---

## Reviewing Triggered Orders

All orders placed by Algo Flows appear in **Orders → Open Orders** and **Trade History** with:
- Source: \`ALGO_FLOW\`
- The strategy name that triggered it

You can cancel any triggered order before it fills from the Orders page.

---

## Strategy Performance

From the strategy detail screen, you can see:

| Metric | Description |
|---|---|
| Total triggers | How many orders this strategy has placed |
| Win rate | % of triggered orders that closed in profit |
| Avg P&L per trade | Average realised P&L per completed trade |
| Last triggered | When the strategy last fired and for which symbol |

Use this to evaluate whether your conditions have a real edge or are just firing on noise.`,
};
